import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Reveal } from "./Reveal";
import { useLang } from "@/i18n";

const CENTER = [15.9827, -61.7152];
const ZOOM = 16;

const PINS = [
  { pos: [15.98392, -61.713681], fr: "Villa Mana", en: "Villa Mana", star: true },
  { pos: [15.982986, -61.716228], fr: "Marina de Rivière Sens", en: "Rivière Sens marina" },
  { pos: [15.980924, -61.715802], fr: "Plage de sable noir", en: "Black-sand beach" },
  { pos: [15.9843, -61.7167], fr: "Commerces & restaurants", en: "Shops & restaurants" },
];

function pinIcon(label, star) {
  return L.divIcon({
    className: "vm-pin",
    html: `<div class="vm-pin-inner${star ? " vm-pin-star" : ""}"><span class="vm-pin-dot"></span><span class="vm-pin-label">${label}</span></div>`,
    iconSize: [0, 0],
  });
}

export default function SituationMap({ itineraryUrl }) {
  const { isFr, t } = useLang();
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const map = L.map(el, {
      center: CENTER,
      zoom: ZOOM,
      zoomControl: false,
      scrollWheelZoom: false,
      dragging: !coarse,
      tap: !coarse,
    });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    PINS.forEach((p) =>
      L.marker(p.pos, { icon: pinIcon(isFr ? p.fr : p.en), keyboard: false }).addTo(map)
    );
    L.control.zoom({ position: "bottomright" }).addTo(map);
    return () => map.remove();
  }, [isFr]);

  return (
    <Reveal delay={0.15}>
      <div className="mt-14" data-testid="situation-map">
        <div className="clipped-frame">
          <div
            ref={ref}
            data-testid="situation-map-canvas"
            className="w-full h-[320px] sm:h-[440px] relative z-0"
          />
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
          <p
            className="font-mono text-[0.55rem] uppercase tracking-[0.25em] text-sage max-w-md leading-relaxed"
            data-testid="situation-map-caption"
          >
            {t.situation.mapCaption}
          </p>
          <a
            href={itineraryUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="situation-map-itinerary"
            className="border border-gold/50 text-gold hover:bg-gold hover:text-night px-6 py-3 font-mono text-[0.6rem] uppercase tracking-[0.22em] transition-all duration-300"
          >
            {t.situation.itinerary} ↗
          </a>
        </div>
      </div>
    </Reveal>
  );
}
